import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  Edit3,
  Trash2,
  MoreVertical,
  Search,
  Filter,
} from 'lucide-react';
import { Button, Card, Input, Dropdown } from '../components/design-system';
import { getTemplateById } from '../data/planTemplates';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

const STATUS_ICONS = {
  draft: Edit3,
  in_progress: Clock,
  completed: CheckCircle,
};

export const PlansPage = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/plans', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!confirm(t('plans.confirmDelete'))) return;
    
    try {
      const response = await fetch(`/api/plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });
      if (response.ok) {
        setPlans(plans.filter(p => p.id !== planId));
      }
    } catch (error) {
      console.error('Failed to delete plan:', error);
    }
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusLabel = (status) => {
    return t(`plans.status.${status}`);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('plans.title')}</h1>
            <p className="text-gray-600 mt-1">{t('plans.subtitle')}</p>
          </div>
          <Link to="/plans/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {t('plans.createNew')}
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder={t('plans.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="flex gap-2">
            {['all', 'draft', 'in_progress', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? t('plans.filterAll') : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Plans Grid */}
        {filteredPlans.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {plans.length === 0 ? t('plans.empty.title') : t('plans.noResults.title')}
            </h3>
            <p className="text-gray-600 mb-6">
              {plans.length === 0 ? t('plans.empty.description') : t('plans.noResults.description')}
            </p>
            {plans.length === 0 && (
              <Link to="/plans/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('plans.createFirst')}
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredPlans.map((plan) => {
              const template = getTemplateById(plan.template_id);
              const StatusIcon = STATUS_ICONS[plan.status];
              
              return (
                <Card key={plan.id} className="p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[plan.status]}`}>
                      <StatusIcon className="h-3 w-3 inline mr-1" />
                      {getStatusLabel(plan.status)}
                    </div>
                    <Dropdown
                      trigger={
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <MoreVertical className="h-4 w-4 text-gray-500" />
                        </button>
                      }
                      items={[
                        { label: t('common.edit'), onClick: () => window.location.href = `/plans/${plan.id}` },
                        { label: t('common.delete'), onClick: () => deletePlan(plan.id), danger: true },
                      ]}
                    />
                  </div>
                  
                  <Link to={`/plans/${plan.id}`}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-primary-600">
                      {plan.title}
                    </h3>
                  </Link>
                  
                  {template && (
                    <p className="text-sm text-gray-500 mb-3">
                      {t(template.nameKey)}
                    </p>
                  )}
                  
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="h-3 w-3 mr-1" />
                    {new Date(plan.updated_at).toLocaleDateString()}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
